import { useCallback, useEffect, useMemo, useState } from 'react';
import { filterByWord } from '@/utils/global';
import TagSelectorDropDownComponent, {
  GroupedTagSelectorDropDownProps,
  isTagSelectorOption,
} from './GroupedTagSelectorDropdown.component';
import { TagSelectorOption } from './TagSelectorDropDown.component';

export type TagSelectorType<Option> = Pick<
  GroupedTagSelectorDropDownProps<Option>,
  | 'name'
  | 'label'
  | 'selectorLimit'
  | 'isLabelAboveField'
  | 'errorMessage'
  | 'onChangeDropDown'
  | 'disableDeleteAll'
  | 'isDisabled'
> & {
  options?: GroupedTagSelectorDropDownProps<Option>['allOptions'];
  defaultValue?: string[];
};

const TagSelectorDropDownContainer = function <Option extends TagSelectorOption | string>({
  name,
  label,
  options = [],
  selectorLimit,
  isLabelAboveField,
  isDisabled,
  errorMessage,
  onChangeDropDown,
  disableDeleteAll,
  defaultValue = [],
  ...props
}: TagSelectorType<Option>): JSX.Element {
  const [isTagSelectorOpened, setIsTagSelectorOpened] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<string[]>(defaultValue || []);

  useEffect(() => {
    setSelectedOptions(defaultValue || []);
  }, [defaultValue]);

  const clearInput = (): void => {
    setInputValue('');
  };

  const formattedName = useMemo(() => {
    return (name ?? '').replace(/\s/g, '');
  }, [name]);

  const toggleTagSelectorHandler = (): void => {
    setIsTagSelectorOpened(!isTagSelectorOpened);
    clearInput();
  };

  const onRemoveItem = (index: number): void => {
    const doesIndexMatch = (option, optionIndex): boolean => optionIndex !== index;
    const filteredOptions = selectedOptions.filter(doesIndexMatch);

    setSelectedOptions(filteredOptions);

    onChangeDropDown?.({
      target: { value: filteredOptions, id: name, name },
      type: 'change',
    });
  };

  const removeAllOptionsHandler = (): void => {
    onChangeDropDown?.({
      target: { value: [], id: name, name },
      type: 'change',
    });
    setSelectedOptions([]);
    clearInput();
  };

  const selectedOptionsHandler = (option: string): void => {
    if (selectedOptions.includes(option)) {
      return;
    }

    onChangeDropDown?.({
      target: { value: [...selectedOptions, option], id: name, name },
      type: 'change',
    });

    setSelectedOptions([...selectedOptions, option]);
    setIsTagSelectorOpened(!isTagSelectorOpened);
    clearInput();
  };

  const onChangeDropDownHandler = useCallback(
    (event: any): void => {
      if (!isTagSelectorOpened) {
        setIsTagSelectorOpened(true);
      }

      setInputValue(event.target.value);
    },
    [isTagSelectorOpened]
  );

  useEffect(() => {
    setSelectedOptions((prevOptions) => {
      return prevOptions.filter((_, index) =>
        selectorLimit === undefined ? true : index < selectorLimit
      );
    });
  }, [selectorLimit]);

  const onPageClick = useCallback((event): void => {
    const tagSelectorId = 'tag-selector';
    const isTagSelector = (element): boolean => element?.id === tagSelectorId;
    const eventPath = event.composedPath() ?? [];
    const isClickingInsideTagSelector = eventPath.some(isTagSelector);

    if (!isClickingInsideTagSelector) {
      setIsTagSelectorOpened(false);
    }
  }, []);

  useEffect(() => {
    if (!isTagSelectorOpened) {
      return () => {};
    }

    window.addEventListener('click', onPageClick);
    return () => window.removeEventListener('click', onPageClick);
  }, [onPageClick, isTagSelectorOpened]);

  const getOptionLabel = (option: Option): string => {
    return isTagSelectorOption(option) ? option.label : option;
  };

  const getOptionValue = (option: Option): string => {
    return isTagSelectorOption(option) ? option.value : option;
  };

  const filteredOptions = (options || [])
    .map<(typeof options)['0']>((group) => ({
      label: group.label,
      options: group.options.filter((option) => {
        const found = !inputValue ? true : filterByWord(getOptionLabel(option), inputValue);
        const notSelected = !defaultValue.some((value) => getOptionValue(option) === value);

        return found && notSelected;
      }),
    }))
    .filter((group) => group.options.length > 0);

  const placeholder =
    selectorLimit === undefined || isTagSelectorOpened
      ? 'Search for an option...'
      : `Choose up to ${selectorLimit}`;

  return (
    <TagSelectorDropDownComponent
      isDisabled={isDisabled}
      errorMessage={errorMessage}
      name={formattedName}
      label={label}
      disableDeleteAll={disableDeleteAll}
      selectorLimit={selectorLimit}
      inputValue={inputValue}
      filteredOptions={filteredOptions}
      allOptions={options}
      selectedOptions={selectedOptions}
      isLabelAboveField={isLabelAboveField}
      isTagSelectorOpened={isTagSelectorOpened}
      onChangeDropDown={onChangeDropDownHandler}
      selectedOptionsHandler={selectedOptionsHandler}
      toggleTagSelectorHandler={toggleTagSelectorHandler}
      onRemoveItem={onRemoveItem}
      removeAllOptionsHandler={removeAllOptionsHandler}
      placeholder={placeholder}
      {...props}
    />
  );
};

export default TagSelectorDropDownContainer;
